<template>
    <div class="pause-overlay">
        <div v-if="mode === 'od'" class="media">
            <div class="logos">
                <img
                    class="logo movie"
                    :src="logoUrl"
                />
            </div>

            <div v-if="odSegments.length" class="detail od">
                <span
                    v-for="(segment, i) in odSegments"
                    :key="i"
                    class="segment"
                    :class="segment.type"
                >
                    <template v-if="segment.type === 'rating'">
                        <span class="star">★</span>{{ segment.value }}
                    </template>
                    <template v-else>{{ segment.value }}</template>
                </span>
            </div>

            <div class="watching movie">
                {{media.overview}}
            </div>
        </div>


        <div v-if="mode === 'epg' || mode === 'live'" class="media">
            <div class="logos">
                <img
                    class="logo channel"
                    :src="media.asset.img"
                />
            </div>

            <div class="watching">
                You're watching {{media.name}} live on QB
            </div>
        </div>


        <div v-if="mode === 'sport'" class="media">
            <div v-if="media.isTeamSport" class="logos">
                <template v-for="(team, i) in teams">
                    <span v-if="i > 0" :key="'vs-' + team.id" class="vs">vs.</span>
                    <img
                        v-if="team.logo"
                        :key="team.id"
                        class="logo sport"
                        :src="team.logo"
                        :alt="team.name"
                    />
                    <span
                        v-else
                        :key="team.id"
                        class="logo fallback"
                        :style="{ backgroundColor: team.color }"
                    >
                        {{ team.abbr }}
                    </span>
                </template>
            </div>
            <div v-else>
                <img
                    class="logo sport individual"
                    @error="sportsLogoError"
                    :src="media.logos[0]"
                />
            </div>

            <div v-if="detailSegments.length" class="detail">
                <span v-for="(segment, i) in detailSegments" :key="i" class="segment">
                    {{ segment }}
                </span>
            </div>

            <div v-if="sportsWatchingText" class="watching">
                {{ sportsWatchingText }}
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'PauseOverlay',
    props: {
        loading: Boolean,
        media: Object,
        mode: String
    },
    data () {
        return {
            logoUrl: ''
        }
    },
    mounted(){
        if(this.mode === 'od'){
            this.loadLogo()
        }
    },

    methods:{
        sportsLogoError: function(e){
            const sport = window.sportsData.find((x) => { return x.name === this.media.sportName})
            e.target.src = sport.img
        },
        async loadLogo(){
            const req = await window.afetch(`/logo?id=${this.media.id}&type=${this.media.type}`)
            const res = await req.json()
            this.logoUrl = res.logo
        }

    },

    computed: {
        isTv () {
            return !!this.media && this.media.type === 'tv'
        },
        odYear () {
            const m = this.media || {}
            if (this.isTv) {
                const start = (m.first_air_date || '').slice(0, 4)
                const end = (m.last_air_date || '').slice(0, 4)
                if (start && m.status === 'Ended' && end && end !== start) {
                    return `${start}-${end}`
                }
                return start
            }
            return m.year || (m.release_date || '').slice(0, 4)
        },

        odLength () {
            const m = this.media || {}
            if (this.isTv) {
                const n = m.number_of_seasons
                return n ? `${n} ${n === 1 ? 'Season' : 'Seasons'}` : ''
            }
            if (!m.runtime) return ''
            const h = Math.floor(m.runtime / 60)
            const min = m.runtime % 60
            return [h && `${h}h`, min && `${min}m`].filter(Boolean).join(' ')
        },
        odRating () {
            const n = Number(this.media && this.media.vote_average)
            return n > 0 ? n.toFixed(1) : ''
        },
        odCertification () {
            const m = this.media || {}
            if (m.certification) return m.certification
            const us = list => (list || []).find(r => r.iso_3166_1 === 'US')
            if (this.isTv) {
                const r = us(m.content_ratings && m.content_ratings.results)
                return (r && r.rating) || ''
            }
            const r = us(m.release_dates && m.release_dates.results)
            const rel = r && (r.release_dates || []).find(d => d.certification)
            return (rel && rel.certification) || ''
        },
        odSegments () {
            return [
                { type: 'text', value: this.odYear },
                { type: 'text', value: this.odLength },
                { type: 'rating', value: this.odRating },
                { type: 'badge', value: this.odCertification }
            ].filter(s => s.value)
        },

        competition () {
            const list = this.media && this.media.competitions
            return (list && list[0]) || {}
        },

        teams () {
            const list = this.competition.competitors || []
            return list
                .slice()
                .sort((a, b) => (a.homeAway === 'away' ? 0 : 1) - (b.homeAway === 'away' ? 0 : 1))
                .map(c => {
                    const team = c.team || {}
                    return {
                        id: team.id || c.id,
                        name: team.displayName || team.name || '',
                        abbr: team.abbreviation || '',
                        logo: team.logo || '',
                        color: team.color ? '#' + team.color : '#333',
                        score: c.score
                    }
                })
        },
        status () {
            return this.competition.status || (this.media && this.media.status) || {}
        },
        state () {
            return (this.status.type && this.status.type.state) || ''
        },
        isLive () {
            return this.state === 'in'
        },
        league () {
            const m = this.media || {}
            if (m.kind && m.kind.indexOf('/') > -1) return m.kind.split('/')[1].toUpperCase()
            const match = (m.sportLogo || m.logo || '').match(/\/([a-z0-9]+)\.\w+$/i)
            if (match) return match[1].toUpperCase()
            return m.sportName || ''
        },
        scoreText () {
            const detail = this.status.type && this.status.type.shortDetail
            if (this.state === 'pre') return detail || ''
            const scores = this.teams.map(t => t.score).filter(s => s !== undefined && s !== null)
            if (scores.length < 2) return detail || ''

            return scores.join(' - ') + (detail ? ` (${detail})` : '')
        },
        stadium () {
            return (this.competition.venue && this.competition.venue.fullName) || ''
        },
        detailSegments () {
            return [this.league, this.scoreText, this.stadium].filter(Boolean)
        },
        sportsWatchingText () {
            if(!this.media.isTeamSport){
                return `You're watching ${this.media.sportName} ${this.media.name} ${this.isLive ? ' live' : ''} on QB`
            }else{
                let subject = ''
                if (this.teams.length >= 2) {
                    subject = this.teams.map(t => t.name).join(' vs ')
                } else if (this.media && this.media.name) {
                    subject = this.media.name
                }
                if (!subject) return ''
                return `You're watching ${subject}${this.isLive ? ' live' : ''} on QB`
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.pause-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    pointer-events: none;
    z-index: 2;
    background: linear-gradient(90deg, rgba(#000, 0.75) 0%, rgba(#000, 0.25) 100%);
    display: flex;
    align-items: center;
    padding: 0 48px;
    color: #fff;
}

.media {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.logos {
    display: flex;
    align-items: center;
    gap: 32px;
}

.logo {
    object-fit: contain;
    width: 160px;
    padding-bottom: 24px;

    &.movie{
        width: 500px;
    }
    &.channel{
        width: 200px;
        height: auto;
    }
    &.sport{
        width: 160px;
        height: 160px;
        &.individual{
            width: auto;
            height: 260px;
        }
    }

    &.fallback {
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        font-size: 48px;
        font-weight: 700;
    }
}

.vs {
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    opacity: 0.6;
}

.detail {
    display: flex;
    flex-wrap: wrap;
    font-size: 10px;
    font-weight: 600;
}

.segment + .segment::before {
    content: ' ';
    margin: 0 8px;
    opacity: 0.4;
}

// movie / tv meta row: 2026 · 1h 43m · ★ 6.8 · [R]
.detail.od {
    align-items: center;
    font-size: 12px;
    font-weight: 600;
    color: rgba(#fff, 0.7);

    .segment {
        display: inline-flex;
        align-items: center;
    }

    .segment + .segment::before {
        content: '·';
        margin: 0 14px;
        opacity: 0.5;
    }

    .star {
        color: #d9a526;
        margin-right: 8px;
    }

    .badge {
        padding: 2px 10px;
        border: 2px solid rgba(#fff, 0.4);
        border-radius: 4px;
        font-size: 16px;
        line-height: 1.3;
        color: rgba(#fff, 0.85);
    }
}

.watching {
    max-width: 500px;
    font-size: 16px;
    margin-top: 24px;
    line-height: 1.5;
    &.movie{
        max-width: 700px;
        font-size: 14px;
        opacity: 0.8;

    }
}
</style>
